export const PLAYGROUND_SYM = "627";

// Deliberately public demo credentials, published for the NaaS Playground:
// https://docs.libum.io/nexum/symxchange/credentials
export const PLAYGROUND_CREDENTIALS = {
  AdministrativeCredentials: { Password: "suwn37834b91&" },
  UserNumberCredentials: { UserNumber: 1995, Password: "thiswillwerkOP00!" },
  AccountNumberCredentials: {
    AccountNumber: "0000013824",
    HomeBankingPassword: "Password123!",
  },
  HomeBankingCredentials: { UserName: "TEST13824", Password: "Password123!" },
  CardCredentials: { CardNumber: "1234567891234567" },
  MICRCredentials: { MICRNumber: 12345678 },
  LookupCredentials: {
    Type: 0,
    Value: "TESTING",
    HomeBankingPassword: "Password123!",
  },
} as const;

/** Pick one supported credential type; don't send every schema alternative. */
export function playgroundExample(value: unknown): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const body = value as Record<string, unknown>;
  const credentials = body.Credentials;
  if (
    !credentials ||
    typeof credentials !== "object" ||
    Array.isArray(credentials)
  )
    return value;
  for (const [name, published] of Object.entries(PLAYGROUND_CREDENTIALS)) {
    if (Object.hasOwn(credentials, name))
      return { ...body, Credentials: { [name]: { ...published } } };
  }
  // User-management operations also expose the credential fields directly.
  if (
    Object.hasOwn(credentials, "UserNumber") &&
    Object.hasOwn(credentials, "Password")
  )
    return {
      ...body,
      Credentials: {
        ...credentials,
        ...PLAYGROUND_CREDENTIALS.UserNumberCredentials,
      },
    };
  if (
    Object.hasOwn(credentials, "UserName") &&
    Object.hasOwn(credentials, "Password")
  )
    return {
      ...body,
      Credentials: {
        ...credentials,
        ...PLAYGROUND_CREDENTIALS.HomeBankingCredentials,
      },
    };
  // Expiring TokenCredentials must come from a login; never invent a token.
  return value;
}

/** Refresh old example placeholders without replacing edited credentials or payload fields. */
export function refreshPlaygroundDraft(
  text: string,
  operationKey = "",
  defaultExample?: string,
): string {
  try {
    const body = JSON.parse(text);
    const credentials = body?.Credentials;
    if (
      !credentials ||
      typeof credentials !== "object" ||
      Array.isArray(credentials)
    )
      return text;
    let changed = false;
    if (
      operationKey.endsWith("/getAccountSelectFields") &&
      body.AccountNumber === "0000000000"
    ) {
      body.AccountNumber = "0000013824";
      changed = true;
    }
    // Update only known read examples, using their authored defaults. Preserve user edits.
    const knownRead =
      /\/account\/(?:getAccountSelectFields(?:FilterChildren)?|(?:get|search)(?:Card|Loan|Name|Share|Tracking)(?:SelectFields|PagedListSelectFields|PagedSelectFields))$/.test(
        operationKey,
      );
    if (knownRead && defaultExample) {
      const defaults = JSON.parse(defaultExample);
      const patch = (
        current: Record<string, any>,
        expected: Record<string, any>,
      ) => {
        for (const [field, next] of Object.entries(expected)) {
          const old = current[field];
          if (
            next &&
            typeof next === "object" &&
            !Array.isArray(next) &&
            old &&
            typeof old === "object" &&
            !Array.isArray(old)
          )
            patch(old, next);
          else if (
            (field === "AccountNumber" && old === "0000000000") ||
            (/^(ShareId|LoanId|CardLocator|NameLocator|TrackingLocator|NumberOfRecordsToReturn)$/.test(
              field,
            ) &&
              (old === 0 || old === "0")) ||
            (field === "Query" && old === "string")
          ) {
            current[field] = next;
            changed = true;
          }
        }
      };
      if (
        Object.hasOwn(body, "Query") &&
        defaults.SearchFilter &&
        !body.SearchFilter
      ) {
        body.SearchFilter = {
          Query:
            body.Query === "string" ? defaults.SearchFilter.Query : body.Query,
        };
        delete body.Query;
        changed = true;
      }
      if (body.PagingRequestContext?.Token === "string") {
        delete body.PagingRequestContext.Token;
        changed = true;
      }
      patch(body, defaults);
    }
    const refresh = (
      target: unknown,
      published: Record<string, string | number>,
    ) => {
      if (!target || typeof target !== "object" || Array.isArray(target))
        return;
      const fields = target as Record<string, unknown>;
      for (const [field, value] of Object.entries(published)) {
        if (
          typeof fields[field] === "string" &&
          (fields[field] as string).startsWith("REPLACE_WITH_YOUR_")
        ) {
          fields[field] = value;
          changed = true;
        }
      }
    };
    for (const [type, published] of Object.entries(PLAYGROUND_CREDENTIALS))
      refresh(credentials[type], published);
    if (Object.hasOwn(credentials, "UserNumber"))
      refresh(credentials, PLAYGROUND_CREDENTIALS.UserNumberCredentials);
    if (Object.hasOwn(credentials, "UserName"))
      refresh(credentials, PLAYGROUND_CREDENTIALS.HomeBankingCredentials);
    return changed ? JSON.stringify(body, null, 2) : text;
  } catch {
    // An in-progress JSON edit must remain editable, even when temporarily invalid.
    return text;
  }
}
